/**
 * Bridges Potree (bundled THREE) and Spark (ESM THREE) so paged LoD uses correct
 * camera pose and splat world matrices.
 */

/**
 * @param {import("../../viewer/viewer.js").Viewer} viewer
 * @param {object} spark - SparkRenderer
 * @param {object} splatMesh - SplatMesh
 * @param {object} [options]
 * @param {boolean} [options.forceLodCameraSync=true]
 * @param {boolean} [options.syncWorldMatrix=true]
 * @param {boolean} [options.driveSparkUpdate=true]
 */
export function installPotreeSparkIntegration (viewer, spark, splatMesh, options = {}) {
	const PTHREE = Potree.THREE;
	const forceLodCameraSync = options.forceLodCameraSync !== false;
	const syncWorldMatrix = options.syncWorldMatrix !== false;
	const driveSparkUpdate = options.driveSparkUpdate !== false;

	const lodCamPos = new PTHREE.Vector3();
	const lodCamQuat = new PTHREE.Quaternion();
	const lodCamPosLast = new PTHREE.Vector3();
	const localMatrix = new PTHREE.Matrix4();
	const worldMatrix = new PTHREE.Matrix4();
	const localPos = new PTHREE.Vector3();
	const localQuat = new PTHREE.Quaternion();
	const localScale = new PTHREE.Vector3();

	let sparkUpdatePending = false;

	const syncSplatWorldMatrix = () => {
		if (!syncWorldMatrix || !splatMesh) {
			return;
		}

		localPos.set(splatMesh.position.x, splatMesh.position.y, splatMesh.position.z);
		localQuat.set(
			splatMesh.quaternion.x,
			splatMesh.quaternion.y,
			splatMesh.quaternion.z,
			splatMesh.quaternion.w
		);
		localScale.set(splatMesh.scale.x, splatMesh.scale.y, splatMesh.scale.z);
		localMatrix.compose(localPos, localQuat, localScale);

		const parent = splatMesh.parent;
		if (parent) {
			parent.updateMatrixWorld(true);
			worldMatrix.multiplyMatrices(parent.matrixWorld, localMatrix);
		} else {
			worldMatrix.copy(localMatrix);
		}

		splatMesh.matrixWorld.fromArray(worldMatrix.elements);
		splatMesh.matrix.fromArray(localMatrix.elements);
		splatMesh.matrixWorldNeedsUpdate = false;
	};

	const syncSparkLodCamera = () => {
		if (!forceLodCameraSync) {
			spark.lodPosOverride = null;
			spark.lodQuatOverride = null;
			return;
		}

		const cam = viewer.scene.getActiveCamera();
		viewer.scene.scene.updateMatrixWorld(true);
		syncSplatWorldMatrix();
		cam.updateMatrixWorld(true);
		cam.getWorldPosition(lodCamPos);
		cam.getWorldQuaternion(lodCamQuat);
		if (![lodCamPos.x, lodCamPos.y, lodCamPos.z, lodCamQuat.x, lodCamQuat.y, lodCamQuat.z, lodCamQuat.w].every(Number.isFinite)) {
			return;
		}

		spark.lodPosOverride = lodCamPos;
		spark.lodQuatOverride = lodCamQuat;

		if (lodCamPosLast.distanceToSquared(lodCamPos) > 1e-4) {
			spark.lodDirty = true;
			lodCamPosLast.copy(lodCamPos);
		}
	};

	const queueSparkUpdate = () => {
		if (!driveSparkUpdate || sparkUpdatePending) {
			return;
		}
		sparkUpdatePending = true;
		requestAnimationFrame(async () => {
			sparkUpdatePending = false;
			try {
				spark.lodDirty = true;
				await spark.update({
					scene: viewer.scene.scene,
					camera: viewer.scene.getActiveCamera()
				});
			} catch (error) {
				console.warn("[PotreeSpark] spark.update failed", error);
			}
		});
	};

	const onUpdate = () => {
		syncSparkLodCamera();
		queueSparkUpdate();
	};

	viewer.addEventListener("update", onUpdate);

	return {
		syncSparkLodCamera,
		syncSplatWorldMatrix,
		dispose () {
			viewer.removeEventListener("update", onUpdate);
			spark.lodPosOverride = null;
			spark.lodQuatOverride = null;
		}
	};
}

/**
 * @param {object} sparkThree - THREE namespace used by Spark (importmap).
 * @returns {boolean}
 */
export function isPotreeSparkThreeUnified (sparkThree) {
	return typeof Potree !== "undefined"
		&& Potree.THREE != null
		&& sparkThree != null
		&& Potree.THREE === sparkThree;
}
